import React from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useState, useEffect, useRef } from 'react';
import { users, auth } from '../services/api';

const Nav = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  
  // Profile modal state
  const [showProfile, setShowProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', email: '', phone: '' });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '' });
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isSuperadmin = user?.role === 'superadmin';
  
  // Fetch user's allowed tabs from server
  const [allowedTabs, setAllowedTabs] = useState(null); // null = all allowed
  
  useEffect(() => {
    if (user?.id) {
      fetch(`/api/users/${user.id}/tabs`, { credentials: 'include' })
        .then(r => r.json())
        .then(data => {
          setAllowedTabs(data.tabs); // null or array
        })
        .catch(() => {});
    }
  }, [user?.id]);
  
  // Check if tab is allowed
  const canAccess = (tabId) => {
    if (!allowedTabs) return true; // No restrictions
    return allowedTabs.includes(tabId);
  };
  

  const isActive = (path) => location.pathname === path;

  // Load theme on mount - dark mode only
  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
    localStorage.setItem('theme', 'dark');
  }, []);

  // WebSocket for real-time messages
  const wsRef = useRef(null);
  const fetchCountsRef = useRef(null);
  const [chatCount, setChatCount] = useState(0);
  const [contactCount, setContactCount] = useState(0);
  
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const [chatsRes, contactsRes] = await Promise.all([
          fetch('/api/chats'),
          fetch('/api/contacts')
        ]);
        const chatsData = await chatsRes.json();
        const contactsData = await contactsRes.json();
        setChatCount(chatsData.filter(c => !c.read).length);
        setContactCount(contactsData.filter(c => !c.read).length);
      } catch (e) {}
    };
    fetchCountsRef.current = fetchMessages;
    fetchMessages();
    
    // WebSocket for real-time updates
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = protocol + '//' + window.location.host + '/ws';
    
    const connectWS = () => {
      try {
        wsRef.current = new WebSocket(wsUrl);
        wsRef.current.onopen = () => console.log('[Nav] WebSocket connected');
        wsRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'new_chat' || data.type === 'chat_message') {
              console.log('[Nav] New message via WebSocket');
              if (fetchCountsRef.current) fetchCountsRef.current();
            }
          } catch (e) {}
        };
        wsRef.current.onclose = () => { setTimeout(connectWS, 3000); };
        wsRef.current.onerror = (err) => console.error('[Nav] WS error:', err);
      } catch (e) { setTimeout(connectWS, 3000); }
    };
    
    connectWS();
    // Fallback polling every 30 seconds
    const pollInterval = setInterval(fetchMessages, 30000);
    return () => { if (wsRef.current) wsRef.current.close(); clearInterval(pollInterval); };
  }, []);

  // Load profile when modal opens
  useEffect(() => {
    if (showProfile && user) {
      console.log('Loading profile for user:', user);
      users.me()
        .then(data => {
          console.log('Profile data loaded:', data);
          setProfileForm({ 
            name: data.name || '', 
            email: data.email || '', 
            phone: data.phone || '' 
          });
        })
        .catch(err => {
          console.error('Failed to load profile:', err);
          setProfileError('Failed to load profile: ' + (err.message || 'unknown error'));
        });
    }
  }, [showProfile, user]);

  const handleProfileSave = async () => {
    setProfileError('');
    setProfileSuccess('');
    try {
      await users.updateProfile(profileForm);
      setProfileSuccess('Profile updated!');
      setTimeout(() => setShowProfile(false), 1000);
    } catch (err) {
      setProfileError(err.response?.data?.error || err.message || 'Failed to update profile');
    }
  };

  const handlePasswordChange = async () => {
    setProfileError('');
    setProfileSuccess('');
    try {
      await auth.changePassword(passwordForm);
      setProfileSuccess('Password changed!');
      setPasswordForm({ currentPassword: '', newPassword: '' });
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to change password';
      setProfileError(msg);
    }
  };

  return (
    <>
    <nav className="nav">
      <div className="nav-content">
        <div className="nav-logo">ICON MISSION CONTROL</div>
        <div className="nav-links">
          <Link to="/" className={`nav-link ${isActive('/') ? 'active' : ''}`}>
            Dashboard
          </Link>
          <Link to="/customers" className={`nav-link ${isActive('/customers') ? 'active' : ''}`}>
            Customers
          </Link>
          <Link to="/entries" className={`nav-link ${isActive('/entries') ? 'active' : ''}`}>
            Time Entries
          </Link>

          {isSuperadmin && canAccess('settings') && (
          <Link to="/settings" className={`nav-link ${isActive('/settings') ? 'active' : ''}`}>
            Settings
          </Link>
          )}
          {isAdmin && canAccess('users') && (
          <Link to="/users" className={`nav-link ${isActive('/users') ? 'active' : ''}`}>
            👥 Users
          </Link>
          )}
          <Link to="/social" className={`nav-link ${isActive('/social') ? 'active' : ''}`}>
            📱 Social
          </Link>
          <Link to="/messages" className={`nav-link ${isActive('/messages') ? 'active' : ''}`}>
            💬 Messages {chatCount + contactCount > 0 && <span style={{ background: '#22c55e', color: 'white', borderRadius: '10px', padding: '2px 6px', fontSize: '11px', marginLeft: '4px' }}>{chatCount + contactCount}</span>}
          </Link>
          {canAccess('sales-pipeline') && (
            <Link to="/sales-pipeline" className={`nav-link ${isActive("/sales-pipeline") ? "active" : ""}`}>
              📊 Pipeline
            </Link>
          )}
          {canAccess('topics') && (
            <Link to="/topics" className={`nav-link ${isActive('/topics') ? 'active' : ''}`}>
              📋 Topics
            </Link>
          )}

        </div>
        <div className="nav-user">
          <button className="btn btn-ghost btn-sm" onClick={() => setShowProfile(true)} title="My Profile">
            👤 {user?.username || user?.name}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={logout}>
            Logout
          </button>
        </div>
      </div>
    </nav>

    {/* Profile Modal */}
    {showProfile && (
      <div className="modal-overlay" onClick={() => setShowProfile(false)}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 450 }}>
          <div className="modal-header">
            <h3>👤 My Profile</h3>
            <button className="btn-close" onClick={() => setShowProfile(false)}>×</button>
          </div>
          <div className="modal-body">
            {profileSuccess && <div className="alert alert-success">{profileSuccess}</div>}
            {profileError && <div className="alert alert-danger">{profileError}</div>}
            
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Name</label>
              <input
                type="text"
                className="form-input"
                value={profileForm.name}
                onChange={e => setProfileForm({ ...profileForm, name: e.target.value })}
              />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Email</label>
              <input
                type="email"
                className="form-input"
                value={profileForm.email}
                onChange={e => setProfileForm({ ...profileForm, email: e.target.value })}
              />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Phone</label>
              <input
                type="tel"
                className="form-input"
                value={profileForm.phone}
                onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })}
              />
            </div>
            <button className="btn btn-primary" onClick={handleProfileSave} style={{ width: '100%', marginBottom: 24 }}>
              Save Profile
            </button>
            
            <hr style={{ margin: '20px 0', borderColor: '#374151' }} />
            
            <h4 style={{ marginBottom: 12 }}>🔐 Change Password</h4>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Current Password</label>
              <input
                type="password"
                className="form-input"
                value={passwordForm.currentPassword}
                onChange={e => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>New Password</label>
              <input
                type="password"
                className="form-input"
                value={passwordForm.newPassword}
                onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              />
            </div>
            <button className="btn btn-secondary" onClick={handlePasswordChange} style={{ width: '100%' }}>
              Change Password
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Nav;